import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Image, 
  Search, 
  Check, 
  Folder, 
  ChevronRight, 
  Home,
  FileText,
  Loader2 
} from 'lucide-react';
import OptimizedImage from '@/components/OptimizedImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MediaSelector({ 
  open, 
  onOpenChange, 
  onSelect, 
  multiple = false,
  fileType = 'image', // 'image', 'pdf'/'document', 'all'
  title = 'Sélectionner un média'
}) {
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([{ id: null, name: 'Racine' }]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Map fileType to API type value
  const getApiType = (type) => {
    if (type === 'pdf') return 'document';
    return type;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch folders
      const foldersRes = await fetch(`${API_URL}/api/media/folders`, { headers });
      const foldersData = await foldersRes.json();
      setFolders(foldersData.filter(f => f.parent_id === currentFolderId));

      // Fetch media
      let url = `${API_URL}/api/media?`;
      if (currentFolderId) {
        url += `folder_id=${currentFolderId}&`;
      }
      if (fileType !== 'all') {
        // Map 'pdf' to 'document' for API compatibility
        const apiType = getApiType(fileType);
        url += `type=${apiType}`;
      }
      
      const mediaRes = await fetch(url, { headers });
      const mediaData = await mediaRes.json();
      setMedia(mediaData);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    } finally {
      setLoading(false);
    }
  }, [currentFolderId, fileType]);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  const navigateToFolder = (folderId, folderName) => {
    setCurrentFolderId(folderId);
    if (folderId === null) {
      setBreadcrumb([{ id: null, name: 'Racine' }]);
    } else {
      const existingIndex = breadcrumb.findIndex(b => b.id === folderId);
      if (existingIndex >= 0) {
        setBreadcrumb(breadcrumb.slice(0, existingIndex + 1));
      } else {
        setBreadcrumb([...breadcrumb, { id: folderId, name: folderName }]);
      }
    }
  };

  const handleSelect = (item) => {
    if (multiple) {
      const isSelected = selectedItems.some(s => s.id === item.id);
      if (isSelected) {
        setSelectedItems(selectedItems.filter(s => s.id !== item.id));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
    } else {
      onSelect(item);
      onOpenChange(false);
    }
  };

  const handleConfirmMultiple = () => {
    if (selectedItems.length > 0) {
      onSelect(selectedItems);
      onOpenChange(false);
      setSelectedItems([]);
    }
  };

  const filteredMedia = media.filter(m => 
    (m.name || m.filename || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFolders = folders.filter(f => 
    (f.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isSelected = (item) => selectedItems.some(s => s.id === item.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-yellow-500" />
            {title}
          </DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="relative flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher..."
            className="pl-9"
          />
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm flex-wrap flex-shrink-0">
          {breadcrumb.map((item, index) => (
            <React.Fragment key={item.id || 'root'}>
              {index > 0 && <ChevronRight className="w-4 h-4 text-slate-400" />}
              <button
                onClick={() => navigateToFolder(item.id, item.name)}
                className={`px-2 py-1 rounded hover:bg-slate-100 transition ${
                  index === breadcrumb.length - 1 ? 'text-yellow-600 font-medium' : 'text-slate-600'
                }`}
              >
                {index === 0 ? <Home className="w-4 h-4" /> : item.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Content - Scrollable area */}
        <div className="flex-1 min-h-0 overflow-y-auto pr-2">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Folders */}
              {filteredFolders.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Dossiers</h4>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {filteredFolders.map(folder => (
                      <button
                        key={folder.id}
                        onClick={() => navigateToFolder(folder.id, folder.name)}
                        className="flex flex-col items-center gap-2 p-3 rounded-lg border hover:bg-slate-50 hover:border-yellow-300 transition"
                      >
                        <Folder className="w-8 h-8 text-yellow-500" />
                        <span className="text-xs text-center truncate w-full">{folder.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Media Items */}
              {filteredMedia.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">
                    Fichiers ({filteredMedia.length})
                  </h4>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                    {filteredMedia.map(item => (
                      <button
                        key={item.id}
                        onClick={() => handleSelect(item)}
                        className={`relative flex flex-col items-center gap-2 p-2 rounded-lg border transition ${
                          isSelected(item)
                            ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-500'
                            : 'hover:bg-slate-50 hover:border-yellow-300'
                        }`}
                      >
                        {isSelected(item) && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        {item.type === 'image' ? (
                          <OptimizedImage
                            src={item.url}
                            alt={item.name || item.filename}
                            className="w-full aspect-square rounded"
                            thumbSize="small"
                          />
                        ) : (
                          <div className="w-full aspect-square bg-slate-100 rounded flex items-center justify-center">
                            <FileText className="w-8 h-8 text-red-500" />
                          </div>
                        )}
                        <span className="text-xs text-center truncate w-full">{item.name || item.filename}</span>
                        {item.category && (
                          <Badge variant="outline" className="text-[10px]">{item.category}</Badge>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {filteredMedia.length === 0 && filteredFolders.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun fichier trouvé</p>
                  <p className="text-sm">Uploadez des fichiers dans la médiathèque</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer for multiple selection */}
        {multiple && (
          <div className="flex items-center justify-between pt-4 border-t flex-shrink-0">
            <span className="text-sm text-slate-500">
              {selectedItems.length} élément(s) sélectionné(s)
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setSelectedItems([]); onOpenChange(false); }}>
                Annuler
              </Button>
              <Button 
                onClick={handleConfirmMultiple}
                disabled={selectedItems.length === 0}
                className="bg-yellow-500 hover:bg-yellow-600"
              >
                Confirmer la sélection
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
